// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//


import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { AccordionPresentationProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { GridItemProps } from "@insite/mobius/GridItem";
import { Accordion, AccordionSection, Button, DataTable, DataTableBody, DataTableCell, DataTableHead, DataTableHeader, DataTableRow, GridContainer, GridItem, Link, TextField } from "@insite/mobius/index";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import Notify from "@rc/App/Components/Notify/Notify";
import getCustomerGeneticKit from "@rc/App/Handlers/GeneticKit/getCustomerGeneticKit";
import notifyGenericKit from "@rc/App/Handlers/GeneticKit/notifyGenericKit";
import searchCustomerGeneticKit, { onSuccess } from "@rc/App/Handlers/GeneticKit/searchCustomerGeneticKit";
import { CustomAppState } from "@rc/App/Store/Reducers";
import { color, xValidateOnSubmit } from "@rc/XDL/Utilities";
import * as cssLinter from "css";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const formId = "geneticKitForm";

const mapStateToProps = (state: CustomAppState) => {
    return {
        activateGeneticKitUrl: getPageLinkByPageType(state, "ActivateGeneticKitPage")?.url,
    };
}

const mapDispatchToProps = {
    getCustomerGeneticKit,
    notifyGenericKit,
    searchCustomerGeneticKit,
}

export interface OwnProps extends WidgetProps {

}

export interface GenericKitResultModel {
    Kit: KitModel;
    Pet: PetModel;
    PetOwner: PetOwnerModel;
    Vet: VetModel;
}

export interface KitModel {
    KitId: string;
    Status: string;
    TrackingNumber: string;
}

export interface PetModel {
    DateOfBirth: string;
    Gender: string;
    Name: string;
    SpayedOrNeutered: string;
}

export interface PetOwnerModel {
    Email: string;
    FirstName: string;
    LastName: string;
    Phone: string;
}

export interface VetModel {
    Email: string;
    FirstName: string;
    LastName: string;
    Report: string;
    Salutation: string;
}

export interface GenericKitModel {
    KitId: string;
    PetName: string;
    PetOwnerEmail: string;
    PetOwnerFName: string;
    PetOwnerLName: string;
}

export interface NotifyKitModel {
    KitId: string;
    Email: string;
}

type Props = HasHistory & OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const enum fields {
}

export interface GeneticKitStyles {
    accordion: AccordionPresentationProps;
    accordionSection: AccordionSectionPresentationProps;
    gridItem: GridItemProps;
    actionsGridItem: GridItemProps;
    centeringWrapper?: InjectableCss;
    geneticKitsInfoMsgStyle: TypographyPresentationProps;
    spinner?: LoadingSpinnerProps;
    navigateWrapper?: InjectableCss;

    clickableTableCell?: DataTableCellProps;
}

export const geneticKitStyles: GeneticKitStyles = {
    accordion: {
        css: css`
            width: 100%;
            margin-bottom: 40px;
        `,
    },
    accordionSection: {
        panelProps: {
            css: css`
                padding: 0;
            `,
        },
        headerProps: {
            css: css`
                &&& button,
                &&& button:focus {
                    padding: 21px 56px 20px 8px;
                    border: initial; /* removing OOTB border */
                    border-bottom: 1px solid ${color("common.border")};
                    margin-bottom: 20px;
                }
            `,
        },
    },
    gridItem: {
        width: [12, 6, 6, 4, 4],
    },
    actionsGridItem: {
        width: [12, 6, 6, 4, 4],
        css: css`
            justify-content: flex-end;
            align-items: flex-end;
            button {
                margin-left: 30px;
            }
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    geneticKitsInfoMsgStyle: {
        css: css`
            margin: 20px 0;
            display: block;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    clickableTableCell: {
        css: css`
            &&& {
                a span {
                    color: ${getColor("primary")} !important;
                    font-weight: 500;
                }
            }
        `,
    },
    navigateWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end
        `,
    },
}

const styles = geneticKitStyles;

const GeneticKit: React.FunctionComponent<Props> = (props: Props) => {
    const {
        getCustomerGeneticKit,
        searchCustomerGeneticKit,
        notifyGenericKit,
        activateGeneticKitUrl,
        history,
    } = props;

    const [isLoading, setIsLoading] = useState(false);
    const [kits, setKits] = useState([] as GenericKitResultModel[]);
    const [error, setError] = useState("");
    const [geneticKitsInfoMsg, setGeneticKitsInfoMsg] = useState("");

    const [kitId, setKitId] = useState("");
    const [petName, setPetName] = useState("");
    const [petOwnerEmail, setPetOwnerEmail] = useState("");
    const [petOwnerFName, setPetOwnerFName] = useState("");
    const [petOwnerLName, setPetOwnerLName] = useState("");

    useEffect(() => {
        loadCustomerGeneticKit();
    }, []);

    const loadCustomerGeneticKit = () => {
        if (!isLoading) {
            doClean("", "", true);
            getCustomerGeneticKit({
                onSuccess: response => {
                    loadKitsSuccess(response);
                },
                onError: error => {
                    setKits([]);
                    doClean("No Kits found that match the search criteria", error.message || (error.data?.description), false);
                }
            });
        }
    }

    const loadKitsSuccess = (response: any) => {
        if (!response?.SearchResult) {
            setKits([]);
            doClean("No Kits found that match the search criteria", response.message || response.description, false);
        } else {
            doClean("", "", false);
            setKits(response.SearchResult);
        }
    }

    const doClean = (msg: string, error: string, isLoading: boolean) => {
        setGeneticKitsInfoMsg(msg);
        setError(error);
        setIsLoading(isLoading);
    }

    const handleSearchSubmit = (event: React.MouseEvent<HTMLFormElement, MouseEvent>) => {
        event.preventDefault();
        const payload = {
            KitId: kitId,
            PetName: petName,
            PetOwnerEmail: petOwnerEmail,
            PetOwnerFName: petOwnerFName,
            PetOwnerLName: petOwnerLName
        } as any;

        const isSearch = Object.keys(payload).filter(item => payload[item]?.length)?.length > 0;
        isSearch ? searchGeneticKit(payload) : loadCustomerGeneticKit();
    }

    const searchGeneticKit = (parameter: GenericKitModel) => {
        doClean("Filtering Customer Kits ... Please Wait", "", true);
        searchCustomerGeneticKit({
            parameter,
            onSuccess: response => {
                loadKitsSuccess(response);
            },
            onError: error => {
                setKits([]);
                doClean("No Kits found that match the search criteria", error.message || (error.data?.description), false);
            }
        });
    }

    const notifyKit = (item: GenericKitResultModel) => {
        const parameter = {
            KitId: item.Kit.KitId,
            Email: item.PetOwner.Email
        };
        doClean("Sending Email Notification...", "", false);
        notifyGenericKit({
            parameter,
            onSuccess: response => {
                doClean("Email Notification Sent", "", false);
            },
            onError: error => {
                doClean("No Kits found that match the search criteria", error.message || (error.data?.description), false);
            }
        })
    }

    const clearFilter = () => {
        setKitId("");
        setPetName("");
        setPetOwnerEmail("");
        setPetOwnerFName("");
        setPetOwnerLName("");
        loadCustomerGeneticKit();
    }

    const navigateToActivatePage = () => {
        if (activateGeneticKitUrl) {
            history.push(activateGeneticKitUrl);
        }
    }

    return (
        <StyledWrapper>
            <StyledWrapper {...styles.navigateWrapper}>
                <Button type="button" variant="primary" onClick={navigateToActivatePage}>{translate("Activate Kit")}</Button>
            </StyledWrapper>
            <Accordion headingLevel={4} {...styles.accordion}>
                <AccordionSection
                    {...styles.accordionSection}
                    title={translate("Filter Results")}
                >
                    <form id="geneticKitForm" onSubmit={handleSearchSubmit} noValidate={true}>
                        <GridContainer>
                            <GridItem {...styles.gridItem}>
                                <TextField
                                    label={translate("Kit Id")}
                                    value={kitId}
                                    onChange={(event) => setKitId(event.target.value)}
                                    data-test-selector="kitId"
                                    type="text"
                                    {...{ formId }}
                                />
                            </GridItem>
                            <GridItem {...styles.gridItem}>
                                <TextField
                                    label={translate("Owner First Name")}
                                    value={petOwnerFName}
                                    onChange={(event) => setPetOwnerFName(event.target.value)}
                                    data-test-selector="petOwnerFName"
                                    type="text"
                                    {...{ formId }}
                                />
                            </GridItem>
                            <GridItem {...styles.gridItem}>
                                <TextField
                                    label={translate("Owner Last Name")}
                                    value={petOwnerLName}
                                    onChange={(event) => setPetOwnerLName(event.target.value)}
                                    data-test-selector="petOwnerLName"
                                    type="text"
                                    {...{ formId }}
                                />
                            </GridItem>
                            <GridItem {...styles.gridItem}>
                                <TextField
                                    label={translate("Pet Name")}
                                    value={petName}
                                    onChange={(event) => setPetName(event.target.value)}
                                    data-test-selector="petName"
                                    type="text"
                                    {...{ formId }}
                                />
                            </GridItem>
                            <GridItem {...styles.gridItem}>
                                <TextField
                                    label={translate("Owner Email")}
                                    value={petOwnerEmail}
                                    onChange={(event) => setPetOwnerEmail(event.target.value)}
                                    data-test-selector="petOwnerEmail"
                                    type="text"
                                    {...{ formId }}
                                />
                            </GridItem>
                            <GridItem {...styles.actionsGridItem}>
                                <Button type="button" variant="secondary" sizeVariant="small" onClick={clearFilter}>{translate("Clear")}</Button>
                                <Button type="submit" variant="primary" sizeVariant="small">{translate("Search")}</Button>
                            </GridItem>
                        </GridContainer>
                    </form>
                </AccordionSection>
            </Accordion>
            {
                isLoading && (
                    <StyledWrapper {...styles.centeringWrapper}>
                        <LoadingSpinner {...styles.spinner} />
                    </StyledWrapper>
                )
            }
            {
                !isLoading && geneticKitsInfoMsg && (
                    <Notify type="info" {...styles.geneticKitsInfoMsgStyle}>
                        {translate(geneticKitsInfoMsg)}
                    </Notify>
                )
            }
            {
                !isLoading && kits?.length > 0 && (
                    <StyledWrapper>
                        <DataTable>
                            <DataTableHead>
                                <DataTableHeader
                                    tight
                                >
                                    {translate("Kit ID")}
                                </DataTableHeader>
                                <DataTableHeader
                                    tight
                                >
                                    {translate("Owner Name")}
                                </DataTableHeader>
                                <DataTableHeader
                                    tight
                                >
                                    {translate("Owner Email")}
                                </DataTableHeader>
                                <DataTableHeader
                                    tight
                                >
                                    {translate("Pet Name")}
                                </DataTableHeader>
                                <DataTableHeader
                                    tight
                                >
                                    {translate("Status")}
                                </DataTableHeader>
                                <DataTableHeader
                                    tight
                                >
                                    {translate("Tracking #")}
                                </DataTableHeader>
                                <DataTableHeader
                                    tight
                                >
                                </DataTableHeader>
                                <DataTableHeader
                                    tight
                                >
                                    {translate("Vet Report")}
                                </DataTableHeader>
                                <DataTableHeader
                                    tight
                                >
                                    {translate("Pet Owner Report")}
                                </DataTableHeader>
                            </DataTableHead>
                            <DataTableBody>
                                {kits.map(item => {
                                    return (
                                        <DataTableRow
                                            key={uniqueId()}
                                            data-test-key={uniqueId()}
                                        >
                                            <DataTableCell {...styles.clickableTableCell}>
                                                <Link href={`/MyAccount/activatekit?KitId=${item.Kit.KitId}`}>{item.Kit.KitId}</Link>
                                            </DataTableCell>
                                            <DataTableCell>{item.PetOwner.FirstName}&nbsp;{item.PetOwner.LastName}</DataTableCell>
                                            <DataTableCell>{item.PetOwner.Email}</DataTableCell>
                                            <DataTableCell>{item.Pet.Name}</DataTableCell>
                                            <DataTableCell>{item.Kit.Status}</DataTableCell>
                                            <DataTableCell>{item.Kit.TrackingNumber}</DataTableCell>
                                            <DataTableCell {...styles.clickableTableCell}>
                                                <Link href={`/MyAccount/activatekit?KitId=${item.Kit.KitId}`}>{translate("Edit")}</Link>
                                            </DataTableCell>
                                            {
                                                item.Vet.Report && (
                                                    <DataTableCell {...styles.clickableTableCell}>
                                                        <Link href={`http://${item.Vet.Report}`}>{translate("View Report")}</Link>
                                                    </DataTableCell>
                                                )
                                            }
                                            {
                                                item.Vet.Report && (
                                                    <DataTableCell {...styles.clickableTableCell}>
                                                        <Link onClick={() => { notifyKit(item) }}>{translate("Email Report")}</Link>
                                                    </DataTableCell>
                                                )
                                            }
                                        </DataTableRow>
                                    )
                                })}
                            </DataTableBody>
                        </DataTable>
                    </StyledWrapper>
                )
            }
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(GeneticKit)),
    definition: {
        group: "XNGAGE" as any,
        displayName: "Genetic Kit",
    },
};

export default widgetModule;
